import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getPromotionPerformanceByDateRange,
  PromotionPerformanceUnavailableError,
  type PromotionPerformanceSnapshot,
} from '../../sales/infra/salesRepoSnapshotsPromo';

export type PromotionKpis = {
  totalUses: number;
  totalDiscount: number;
  conversionRate: number;
  topPromotions: PromotionPerformanceSnapshot['topPromotions'];
};

export type PromotionKpisState = {
  performanceKpis: PromotionKpis | null;
  performanceByPromoId: Map<string, { usesCount: number; discountTotal: number }>;
  loading: boolean;
  /** Stub / RPC ausente — no confundir con "cero usos". */
  unavailable: boolean;
  unavailableMessage: string | null;
  refresh: () => Promise<void>;
};

const UNAVAILABLE_MSG = 'Las métricas de uso todavía no están disponibles.';

/**
 * KPIs de performance promocional — independientes del listado y sus filtros.
 *
 * Se cargan al montar y cuando cambia la sucursal. Se refrescan:
 * - Manualmente vía `refresh()` (pull-to-refresh)
 *
 * NO se recalculan al filtrar/buscar ni tras mutaciones CRUD locales.
 */
export function usePromotionKpis(
  currentBranchId: string | null | undefined
): PromotionKpisState {
  const [snapshot, setSnapshot] = useState<PromotionPerformanceSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const branchRef = useRef<string | null | undefined>(currentBranchId);

  const refresh = useCallback(async () => {
    const branchId = branchRef.current;
    if (!branchId) {
      setSnapshot(null);
      setUnavailable(false);
      setUnavailableMessage(null);
      setLoading(false);
      return;
    }
    const gen = ++generationRef.current;
    setLoading(true);
    try {
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      const data = await getPromotionPerformanceByDateRange(
        branchId,
        from.toISOString(),
        now.toISOString()
      );
      if (gen !== generationRef.current || !mountedRef.current) return;
      setSnapshot(data);
      setUnavailable(false);
      setUnavailableMessage(null);
    } catch (error) {
      if (gen !== generationRef.current || !mountedRef.current) return;
      const isUnavailable =
        error instanceof PromotionPerformanceUnavailableError ||
        (error &&
          typeof error === 'object' &&
          'code' in error &&
          (error as { code?: string }).code === 'promotion_performance_unavailable');
      if (isUnavailable) {
        setSnapshot(null);
        setUnavailable(true);
        setUnavailableMessage(
          error instanceof Error && error.message.trim() ? error.message.trim() : UNAVAILABLE_MSG
        );
      } else {
        console.warn('[usePromotionKpis] No se pudieron cargar los KPIs', error);
        setSnapshot(null);
        setUnavailable(true);
        setUnavailableMessage(UNAVAILABLE_MSG);
      }
    } finally {
      if (gen === generationRef.current && mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    branchRef.current = currentBranchId;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [currentBranchId, refresh]);

  const performanceKpis = useMemo<PromotionKpis | null>(() => {
    if (!snapshot) return null;
    const totalUses = snapshot.topPromotions.reduce(
      (sum, p) => sum + p.usesCount,
      0
    );
    const totalDiscount = snapshot.topPromotions.reduce(
      (sum, p) => sum + p.discountTotal,
      0
    );
    const totalSales =
      snapshot.salesWithDiscountCount + snapshot.salesWithoutDiscountCount;
    const conversionRate =
      totalSales > 0
        ? Math.round((snapshot.salesWithDiscountCount / totalSales) * 100)
        : 0;
    return { totalUses, totalDiscount, conversionRate, topPromotions: snapshot.topPromotions };
  }, [snapshot]);

  const performanceByPromoId = useMemo(() => {
    const map = new Map<string, { usesCount: number; discountTotal: number }>();
    if (snapshot) {
      for (const p of snapshot.topPromotions) {
        if (p.promotionId) {
          map.set(p.promotionId, { usesCount: p.usesCount, discountTotal: p.discountTotal });
        }
      }
    }
    return map;
  }, [snapshot]);

  return {
    performanceKpis,
    performanceByPromoId,
    loading,
    unavailable,
    unavailableMessage,
    refresh,
  };
}
